import React, { useState } from 'react';
import { 
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  Textarea,
  Select,
  SelectItem,
  Chip,
  addToast
} from '@heroui/react';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  FunnelIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { createExercise, deleteExercise } from '../../services/api';
import { exportExercisesToExcel } from '../../utils/excelExport';
import './ExerciseList.css';

const ExerciseList = ({ items, selectedId, onSelectItem, onExerciseCreated, onExerciseDeleted }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [exerciseToDelete, setExerciseToDelete] = useState(null);

  // QA Status configuration
  const qaStatusOptions = [
    { key: 'new', label: 'New', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: SparklesIcon },
    { key: 'wip', label: 'WIP', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: ClockIcon },
    { key: 'passed', label: 'Passed', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircleIcon }
  ];

  const getQaStatusInfo = (status) => {
    return qaStatusOptions.find(option => option.key === status) || qaStatusOptions[0];
  };

  // Filter options
  const filterOptions = [
    { key: 'all', label: 'All Exercises', icon: DocumentTextIcon },
    ...qaStatusOptions.map(option => ({
      key: option.key,
      label: option.label,
      icon: option.icon
    }))
  ];
  
  // Form state - only basic information
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    repetition: 3
  });

  // Phase selection state
  const [selectedPhases, setSelectedPhases] = useState({
    begin: true,
    middle: true,
    end: true
  });

  // Calculate counts per status
  const statusCounts = items.reduce((acc, item) => {
    const itemStatus = item.qaStatus || 'new';
    acc[itemStatus] = (acc[itemStatus] || 0) + 1;
    return acc;
  }, {});
  
  const totalCount = items.length;
  const newCount = statusCounts['new'] || 0;
  const wipCount = statusCounts['wip'] || 0;
  const passedCount = statusCounts['passed'] || 0;

  const filteredItems = items.filter(item => {
    const itemId = String(item.id || '');
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = itemId.toLowerCase().includes(searchLower);
    
    // Status filter
    const itemStatus = item.qaStatus || 'new';
    const matchesStatus = statusFilter === 'all' || itemStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleAddExercise = () => {
    onOpen();
  };

  const handleDeleteClick = (exercise) => {
    setExerciseToDelete(exercise);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!exerciseToDelete) return;

    try {
      await deleteExercise(exerciseToDelete.id);
      
      // Close modal
      onDeleteClose();
      setExerciseToDelete(null);
      
      // Remove from list by calling parent callback
      if (onExerciseDeleted) {
        onExerciseDeleted(exerciseToDelete.id);
      }
      
      // Show success message
      //alert(`Exercise "${exerciseToDelete.title}" deleted successfully!`);
      //Toast.success(`Exercise "${exerciseToDelete.title}" deleted successfully!`);
      addToast({
        title: "Exercise Deleted",
        description: `Exercise "${exerciseToDelete.title}" deleted successfully!`,
        color: "success"
      });
    } catch (error) {
      console.error('Error deleting exercise:', error);
      //alert('Failed to delete exercise. Please try again.');
      //Toast.error('Failed to delete exercise. Please try again.');
      addToast({
        title: "Delete Error",
        description: "Failed to delete exercise. Please try again.",
        color: "danger"
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhaseToggle = (phaseName) => {
    // Prevent unchecking begin
    if (phaseName === 'begin') {
      return;
    }
    setSelectedPhases(prev => ({
      ...prev,
      [phaseName]: !prev[phaseName]
    }));
  };

  // Export to Excel function
  const handleExportToExcel = async () => {
    try {
      // Show loading message
      addToast({
        title: "Exporting...",
        description: "Fetching complete exercise data for export",
        color: "primary"
      });

      // Fetch complete data for each exercise
      const { fetchSingleExcercise } = await import('../../services/api');
      const completeExercises = [];
      
      for (const item of items) {
        try {
          const response = await fetchSingleExcercise(item.id);
          completeExercises.push(response.data);
        } catch (error) {
          console.error(`Failed to fetch complete data for exercise ${item.id}:`, error);
          // Use basic data if complete fetch fails
          completeExercises.push(item);
        }
      }
      
      // Calculate status counts
      const statusCounts = items.reduce((acc, item) => {
        const itemStatus = item.qaStatus || 'new';
        acc[itemStatus] = (acc[itemStatus] || 0) + 1;
        return acc;
      }, {});
      
      // Export to Excel with complete data
      const filename = exportExercisesToExcel(completeExercises, statusCounts);
      
      // Show success message
      addToast({
        title: "Export Successful",
        description: `Exercise data exported to ${filename}`,
        color: "success"
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      addToast({
        title: "Export Error",
        description: "Failed to export exercise data. Please try again.",
        color: "danger"
      });
    }
  };

  const handleSubmit = async () => {
    try {
      // Build referenceMotionData based on selected phases
      const phaseDefinitions = [
        {
          "name": "begin",
          "duration": 1,
          "path": null,
          "format": "TSFrameLog",
          "videoPath": null,
          "movementRules": {},
          "maxAngleLimits": {},
          "feedbackPriority": []
        },
        {
          "name": "middle",
          "duration": 1,
          "path": null,
          "format": "TSFrameLog",
          "videoPath": null,
          "movementRules": {},
          "maxAngleLimits": {},
          "feedbackPriority": []
        },
        {
          "name": "end",
          "duration": 1,
          "path": null,
          "format": "TSFrameLog",
          "videoPath": null,
          "movementRules": {},
          "maxAngleLimits": {},
          "feedbackPriority": []
        }
      ];

      // Filter phases based on user selection
      const referenceMotionData = phaseDefinitions.filter(phase => selectedPhases[phase.name]);

      // Prepare basic exercise data for API
      const exerciseData = {
        id: formData.id,
        title: formData.title,
        description: formData.description,
        repetition: formData.repetition,
        referenceMotionData: referenceMotionData
      };

      console.log('Sending to API:', exerciseData);

      const response = await createExercise(exerciseData);
      const newExercise = response.data;

      //console.log('Exercise created successfully:', newExercise);
      
      // Close modal
      onClose();
      
      // Reset form and phase selection
      setFormData({
        id: '',
        title: '',
        description: '',
        repetition: 3
      });
      setSelectedPhases({
        begin: true,
        middle: true,
        end: true
      });
      
      // Add new exercise to parent list and select it
      if (onExerciseCreated) {
        onExerciseCreated(newExercise);
      } else {
        // Fallback: just select the exercise
        onSelectItem(newExercise.id);
      }
      
      // Optional: Show success message or notification
      console.log(`Exercise #${newExercise.id} created successfully!`);
      
    } catch (error) {
      console.error('Error creating exercise:', error);
      //alert('Failed to create exercise. Please try again.');
      //Toast.error('Failed to create exercise. Please try again.');
      addToast({
        title: "Create Error",
        description: "Failed to create exercise. Please try again.",
        color: "danger"
      });
    }
  };

  return (
    <div className="exercise-list-container">
      {/* Header with Search, Filter and Add Button */}
      <div className="exercise-list-header">
        {/* Search Input - Full Width */}
        <div className="w-full mb-3">
          <div className="search-input-wrapper w-full">
            <MagnifyingGlassIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input w-full"
            />
          </div>
        </div>
        
        {/* Filter Section - Full Width */}
        <div className="w-full mb-2">
          <Select
            size="sm"
            selectedKeys={[statusFilter]}
            onSelectionChange={(keys) => {
              const selectedKey = Array.from(keys)[0];
              setStatusFilter(selectedKey);
            }}
            placeholder="Filter by status"
            className="status-filter w-full"
            color="primary"
            variant="flat"
            startContent={<FunnelIcon className="h-4 w-4" />}
          >
            {filterOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <SelectItem key={option.key} value={option.key}>
                  <div className="flex items-center gap-2">
                    <IconComponent className="h-4 w-4" />
                    {option.label}
                  </div>
                </SelectItem>
              );
            })}
          </Select>
          
          {statusFilter !== 'all' && (
            <div className="mt-2">
              <Chip
                size="sm"
                color="success"
                variant="flat"
                onClose={() => setStatusFilter('all')}
                classNames={{
                  base: "text-white font-medium w-full justify-center",
                  content: "text-white"
                }}
                startContent={
                  <div className="p-0.5 rounded-full bg-white bg-opacity-20">
                    {(() => {
                      const statusInfo = getQaStatusInfo(statusFilter);
                      const IconComponent = statusInfo.icon;
                      return <IconComponent className="h-3 w-3 text-white" />;
                    })()}
                  </div>
                }
              >
                {getQaStatusInfo(statusFilter).label}
              </Chip>
            </div>
          )}
        </div>
        
        {/* Action Buttons - Full Width */}
        <div className="w-full flex flex-col gap-2">
          <Button
            color="success"
            variant="flat"
            size="sm"
            onPress={handleExportToExcel}
            className="export-excel-btn w-full"
            startContent={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            Export Excel
          </Button>
          
          <Button
            color="primary"
            variant="solid"
            size="sm"
            onPress={handleAddExercise}
            className="add-exercise-btn w-full"
            startContent={<PlusIcon className="h-4 w-4" />}
          >
            Add Exercise
          </Button>
        </div>
      </div>

      {/* Status Summary - Compact & Wrapped */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-semibold text-gray-700 dark:text-gray-300">
            Total: {totalCount}
          </span>
          <span className="text-gray-400">|</span>
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <SparklesIcon className="h-3 w-3" />
            <span className="text-gray-600 dark:text-gray-400">{newCount}</span>
          </div>
          <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
            <ClockIcon className="h-3 w-3" />
            <span className="text-gray-600 dark:text-gray-400">{wipCount}</span>
          </div>
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <CheckCircleIcon className="h-3 w-3" />
            <span className="text-gray-600 dark:text-gray-400">{passedCount}</span>
          </div>
          {filteredItems.length !== totalCount && (
            <>
              <span className="text-gray-400">•</span>
              <span className="text-blue-600 dark:text-blue-400 font-medium">
                Showing: {filteredItems.length}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Exercise List */}
      <div className="exercise-list">
        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <h3>No exercises found</h3>
            <p>Try adjusting your search</p>
            <Button
              color="primary"
              variant="bordered"
              onPress={handleAddExercise}
              startContent={<PlusIcon className="h-4 w-4" />}
            >
              Add Your First Exercise
            </Button>
          </div>
        ) : (
          <ul className="item-list">
            {filteredItems.map(item => {
              const statusInfo = getQaStatusInfo(item.qaStatus || 'new');
              const IconComponent = statusInfo.icon;
              
              return (
                <li 
                  key={String(item.id || Math.random())}
                  className={`item-list-item ${String(item.id) === String(selectedId) ? 'selected' : ''}`}
                >
                  <div className="item-content" onClick={() => onSelectItem(item.id)}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className={`p-0.5 rounded-full mt-0.5 flex-shrink-0 ${statusInfo.bgColor}`}>
                          <IconComponent className={`h-3 w-3 ${statusInfo.color}`} />
                        </div>
                        <div className="item-text flex-1">
                          <span className="item-number">Exercise {item.id}</span>
                          <h3 className="item-title">{item.title}</h3>
                        </div>
                      </div>
                      <div className="item-actions ml-1">
                        <Button
                          size="sm"
                          color="danger"
                          variant="light"
                          onPress={(e) => {
                            //e.stopPropagation();
                            handleDeleteClick(item);
                          }}
                          className="delete-btn"
                          isIconOnly
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add Exercise Modal - Basic Information Only */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-blue-500" />
              <h2 className="modal-title">Add New Exercise</h2>
            </div>
            <p className="text-sm text-gray-500">Enter basic exercise information</p>
          </ModalHeader>
          <ModalBody>
            <div className="add-exercise-form">
                             <div className="space-y-4">
                 <Input
                   label="Exercise ID"
                   type="number"
                   min="1"
                   value={formData.id}
                   onChange={(e) => handleInputChange('id', parseInt(e.target.value))}
                   description="Unique identifier for the exercise"
                   isRequired
                 />
                 
                 <Input
                   label="Exercise Title"
                   placeholder="Enter exercise title"
                   value={formData.title}
                   onChange={(e) => handleInputChange('title', e.target.value)}
                   isRequired
                 />
                 
                 <Textarea
                   label="Description"
                   placeholder="Enter exercise description"
                   value={formData.description}
                   onChange={(e) => handleInputChange('description', e.target.value)}
                   rows={3}
                 />
                 
                 <Input
                   label="Repetition Count"
                   type="number"
                   min="1"
                   max="10"
                   value={formData.repetition}
                   onChange={(e) => handleInputChange('repetition', parseInt(e.target.value))}
                   description="Number of times to repeat the exercise"
                 />
                
                <div className="border border-gray-200 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Motion Phases</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="begin-phase"
                        checked={selectedPhases.begin}
                        disabled
                        className="w-4 h-4 rounded border-gray-300 cursor-not-allowed"
                      />
                      <label htmlFor="begin-phase" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        Begin Phase
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">Required</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="middle-phase"
                        checked={selectedPhases.middle}
                        onChange={() => handlePhaseToggle('middle')}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                      />
                      <label htmlFor="middle-phase" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        Middle Phase
                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded">Optional</span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="end-phase"
                        checked={selectedPhases.end}
                        onChange={() => handlePhaseToggle('end')}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                      />
                      <label htmlFor="end-phase" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        End Phase
                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded">Optional</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                    <h4 className="text-sm font-medium text-blue-800">What happens next?</h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    After creating this exercise, you can configure motion data, media files, and advanced settings in the exercise detail panel.
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onPress={handleSubmit}
              isDisabled={!formData.title.trim() || !formData.id}
            >
              Create Exercise
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <TrashIcon className="w-5 h-5 text-red-500" />
              <h2 className="modal-title">Delete Exercise</h2>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>"{exerciseToDelete?.title}"</strong>?
              </p>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <TrashIcon className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-700 font-medium">Warning</p>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  This action cannot be undone. All exercise data, videos, and motion recordings will be permanently deleted.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="default" variant="light" onPress={onDeleteClose}>
              Cancel
            </Button>
            <Button 
              color="danger" 
              onPress={handleDeleteConfirm}
              startContent={<TrashIcon className="h-4 w-4" />}
            >
              Delete Exercise
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ExerciseList;